import { useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { createPDFRaster } from "features/pubsub/entityActions/cubes/pdfRaster"
import { deleteEntitySocket } from "features/entity-caching/project/slice"
import { pdfRasterImageData } from "features/entity-caching/pdf-rasters/thunks"
import sanitizeFileName from "tools/strings/sanitizeFileName"
import { selectUserID } from "user/profile/selectors"
import { zoomToShowPoints } from "features/map/zoom/thunks"
import { encodePdfRasterCorners, IngestDataType } from "@nova/schemas"
import { parseGeospatialPdfCorners } from "@nova/util-geospatial-pdf-parser"
import { v4 as randomUUID } from "uuid"
import { rasterizePDF } from "./module"
import { mockRasterUploaded } from "./PDFRasterSlice"

export const usePDFRaster = () => {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)
    const projectId = useAppSelector(selectActivatedFireId)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const [isUploading, setIsUploading] = useState(false)

    const handleDropPDF = async (file: File) => {
        setIsUploading(true)
        const rasterId = randomUUID()
        try {
            if (!projectId || !workspaceId || !userId)
                throw Error("PDFRaster: Missing projectId or workspaceId or userId.")

            const buffer = await file.bytes()
            const corners = await parseGeospatialPdfCorners(buffer)
            if (!corners) throw Error("PDFRaster: PDF has no geospatial viewport.")

            dispatch(
                zoomToShowPoints({
                    points: corners.map(
                        (corner) => [corner.longitude, corner.latitude] as [number, number]
                    ),
                })
            )

            const sanitizedFileName = sanitizeFileName(
                file.name.toLowerCase().endsWith(".pdf") ? file.name : file.name + ".pdf"
            )

            pdfRasterImageData[rasterId] = await rasterizePDF(buffer.slice())
            dispatch(
                createPDFRaster({
                    data: {
                        id: rasterId,
                        corners,
                        filename: sanitizedFileName,
                        name: sanitizedFileName,
                        createdBy: {
                            userId,
                        },
                    },
                })
            )
            dispatch(mockRasterUploaded({ id: rasterId, pdfBlobUrl: URL.createObjectURL(file) }))

            await fileManagementApi.putFile(buffer, {
                filename: sanitizedFileName,
                tags: {
                    dataType: IngestDataType.PdfRaster,
                    orgId: workspaceId,
                    projectId,
                    rasterId,
                    corners: encodePdfRasterCorners(corners),
                },
            })
        } catch (err) {
            console.error(err)
            delete pdfRasterImageData[rasterId]
            dispatch(deleteEntitySocket({ path: ["PDFRasters", { id: rasterId }] }))
            dispatch(
                setGlobalNotificationContext({
                    message: "Could not parse PDF.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        } finally {
            setIsUploading(false)
        }
    }

    return { handleDropPDF, isUploading }
}
